import { useState, useEffect } from 'react';
import { getCustomerStatisticsWithComparison } from '../services/customerService';
import { MonthlySnapshot } from '../types';

interface UseCustomerStatisticsResult {
  statistics: {
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    averageLTV: number;
  } | null;
  previousSnapshot: MonthlySnapshot | null;
  percentageChanges: {
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    averageLTV: number;
  } | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useCustomerStatistics = (): UseCustomerStatisticsResult => {
  const [statistics, setStatistics] = useState<{
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    averageLTV: number;
  } | null>(null);
  const [previousSnapshot, setPreviousSnapshot] = useState<MonthlySnapshot | null>(null);
  const [percentageChanges, setPercentageChanges] = useState<{
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    averageLTV: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCustomerStatisticsWithComparison();
      setStatistics(result.current);
      setPreviousSnapshot(result.previous);
      setPercentageChanges(result.percentageChanges);
    } catch (err) {
      console.error('Error fetching customer statistics:', err);
      setError('Failed to load customer statistics');
      setStatistics(null);
      setPreviousSnapshot(null);
      setPercentageChanges(null);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    await fetchStatistics();
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  return {
    statistics,
    previousSnapshot,
    percentageChanges,
    loading,
    error,
    refresh
  };
}; 